/**
 * Seção — agrupamento leve dentro de um painel, com título discreto.
 *
 * Igual ao painel no recolhimento: `props.collapsed` diz como nasce, o que o
 * usuário faz depois fica local e não suja o documento.
 */
import { ChangeDetectionStrategy, Component, computed,
  inject, input, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormGroup } from '@angular/forms';
import { NzIconModule } from 'ng-zorro-antd/icon';

import { ScreenNode } from '../../model/node-props';
import { trackByNodeId } from '../../model/screen-index';
import { TranslateTAPipe } from '../translate-ta.pipe';
import { TranslateService } from '@ngx-translate/core';
import { translateTAValue } from '../../../system-utils/pipes/translateTA';
import { NodeHostComponent } from '../node-host.component';
import { NodeRenderer, RenderMode } from '../node-renderer';
import { childLayoutStyles, safeRawStyles, styleTokenClasses } from './child-layout';

@Component({
  selector: 'tem-section-container',
  standalone: true,
  imports: [CommonModule, NzIconModule, NodeHostComponent, TranslateTAPipe],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div
      class="tem-section"
      [class.tem-section--vazio]="isEmpty()"
      [ngClass]="themeClasses()"
      [ngStyle]="rawStyles()">
      @if (title().length > 0) {
        <h3
          class="tem-section__title"
          [class.tem-section__title--clickable]="isCollapsible()"
          [attr.role]="isCollapsible() ? 'button' : null"
          [attr.tabindex]="isCollapsible() ? 0 : null"
          [attr.aria-expanded]="isCollapsible() ? !collapsed() : null"
          (click)="toggle()"
          (keydown.enter)="toggle()"
          (keydown.space)="toggle()">

          <span>{{ title() | translateTA }}</span>

          @if (isCollapsible()) {
            <nz-icon [nzType]="collapsed() ? 'right' : 'down'" />
          }
        </h3>
      }

      @if (!collapsed()) {
        <div class="tem-section__body" [attr.data-vazio]="isEmpty() ? emptyLabel() : null">
          @for (child of children(); track trackBy(0, child)) {
            <div [ngStyle]="layoutOf(child)">
              <tem-node-host [node]="child" [form]="form()" [mode]="mode()" />
            </div>
          }
        </div>
      }
    </div>
  `,
  styleUrl: './section.renderer.scss'
})
export class SectionContainerRenderer implements NodeRenderer<'section'> {

  readonly node = input.required<ScreenNode<'section'>>();
  readonly form = input<FormGroup | null>(null);
  readonly mode = input<RenderMode>('view');

  readonly trackBy = trackByNodeId;

  private readonly userCollapsed = signal<boolean | null>(null);

  readonly children = computed<readonly ScreenNode[]>(() => this.node().children ?? []);


  private readonly translate = inject(TranslateService);

  /**
   * O CORPO está vazio — com título ou sem.
   *
   * São dois casos, e o segundo é o mais comum. Uma seção **recém-criada** não
   * tem título nem filho: `createDefault` só define `collapsible` e
   * `collapsed`, então o `<h3>` nem é desenhado e ela não tem nada — nem borda,
   * nem texto, nem altura. E uma seção **já nomeada** de onde o usuário acabou
   * de tirar os componentes desenha só o `<h3>`, com o corpo em altura zero.
   *
   * A primeira versão desta regra exigia "sem filho E sem título", e errava
   * justamente o segundo. A pergunta certa é sobre o corpo.
   */
  readonly isEmpty = computed<boolean>(() => this.children().length === 0);

  readonly emptyLabel = computed<string>(
    () => translateTAValue(this.translate, 'builderScreen.container.empty')
  );
  readonly title = computed<string>(() => this.node().props.titleKey ?? '');
  readonly isCollapsible = computed<boolean>(() => this.node().props.collapsible === true);

  readonly collapsed = computed<boolean>(() => {
    const chosen = this.userCollapsed();
    if (chosen !== null) {
      return chosen;
    }
    return this.node().props.collapsed === true;
  });

  readonly themeClasses = computed(() => styleTokenClasses(this.node().style));
  readonly rawStyles = computed(() => safeRawStyles(this.node().style));

  toggle(): void {
    if (!this.isCollapsible()) {
      return;
    }
    this.userCollapsed.set(!this.collapsed());
  }

  layoutOf(child: ScreenNode): Readonly<Record<string, string>> {
    return childLayoutStyles(child);
  }
}
